import React from "react";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import ManualContent from "../../components/ManualContent";


const MaterialModels = () => {
  return (
    <MathJaxContext>
      <ManualContent title="Material Models in Speqqle">
      <div id="top" />

<h2>Jump to:</h2>
<ul>
  <li><a href="#what">What is a Material Model?</a></li>
  <li><a href="#outputs">Outputs of Material Models</a></li>
  <li><a href="#line-shapes">Available Line Shapes</a></li>
  <li><a href="#tauc">Tauc-Lorentz</a></li>
  <li><a href="#fano">Fano</a></li>
  <li><a href="#constant">Constant Dielectric Function</a></li>
  <li><a href="#cauchy">Cauchy</a></li>
  <li><a href="#cauchyabs">Cauchy with Absorption</a></li>
  <li><a href="#factorized">Factorized</a></li>
  <li><a href="#negative-drude">Drude-Lorentz with Negative Weight</a></li>
  <li><a href="#differential">Differential Lorentz</a></li>
  <li><a href="#gapped-drude">Gapped Drude</a></li>
  <li><a href="#kkhybrid">KK-Consistent Hybrid Lorentz Power Law</a></li>
</ul>

<h2 id="what">What is a Material Model?</h2>
        <p>
          Material models in <strong>Speqqle</strong> describe how a material
          interacts with light by defining its{" "}
          <strong>optical properties</strong>—most commonly the{" "}
          <em>dielectric function</em> (
          <MathJax inline>{"\\( \\varepsilon \\)"}</MathJax>),{" "}
          <em>refractive index</em> (<code>n + ik</code>), or{" "}
          <em>optical conductivity</em> (
          <MathJax inline>{"\\( \\sigma \\)"}</MathJax>). These quantities are
          interrelated and can be derived from one another, depending on the
          type of experiment. The material model feeds this optical response
          into your simulation or fit of the experimental quantities. Thus these
          models are key components within Experiments, allowing users to
          simulate and fit data in Raman, Reflectivity, Transmission, and
          Ellipsometry setups.
        </p>

        <p>
          You can add <strong>oscillators</strong> to material models via the <strong>Parameters</strong> tab.
          Each row in the model table corresponds to a line shape contributing to the overall dielectric function.
          By default, two Lorentz oscillators are included as placeholders. You can remove them or add more as needed and change the values to match your data.
        </p>

        <p>
            A <strong>Drude oscillator</strong> represents the free-electron response. It describes metallic behavior with parameters:
            <MathJax inline>{"\\( \\omega_p \\)"}</MathJax> (plasma frequency) and {" "}
            <MathJax inline>{"\\( \\gamma \\)"}</MathJax> (scattering rate), and has no resonance frequency
            (<MathJax inline>{"\\( \\omega_0 = 0 \\)"}</MathJax>).
          </p>
          <p>
            A <strong>Lorentz oscillator</strong> represents bound electron or vibrational resonances.
            It includes a resonance frequency <MathJax inline>{"\\( \\omega_0 \\)"}</MathJax>, width {" "}
            <MathJax inline>{"\\( \\gamma \\)"}</MathJax>, and weight {" "}
            <MathJax inline>{"\\( \\omega_p \\)"}</MathJax>.
          </p>

          <details>
          <summary style={{ cursor: "pointer" }}>Advanced Physics: What are Drude and Lorentz Oscillators?</summary>
          <p>
            add more details about Drude here
          </p>
          <p>
            In <strong>Speqqle</strong>, Lorentz oscillators are expressed using a plasma-frequency-like parameter {" "}
            <MathJax inline>{"\\( \\omega_p \\)"}</MathJax>, which defines the oscillator strength. The amplitude
            <MathJax inline>{"\\( A \\)"}</MathJax> is computed internally as:
          </p>
          <MathJax>\[ A = \omega_p^2 \]</MathJax>
          <p>
            The dielectric function for a Lorentz oscillator is then given by:
          </p>
          <MathJax>
  {"\\( \\varepsilon(\\omega) = \\varepsilon_\\infty + \\frac{\\omega_p^2}{\\omega_0^2 - \\omega^2 - i\\gamma\\omega} \\)"}
</MathJax>

          <p>
            This representation preserves causality and is consistent with the Kramers-Kronig relations. It also facilitates comparison
            with free-carrier models and allows the use of consistent parameters across Drude and Lorentz terms.
          </p>
        </details>


        <p><a href="#top">Back to top</a></p>

        <h2>Why Use Material Models?</h2>
        <p>
          You use material models to simulate or fit the behavior of bulk,
          layered structures or thin films in an optical experiment. By choosing
          appropriate dielectric functions and line shapes, you can match
          experimental data and extract physical properties such as carrier
          concentrations, band gaps, or phonon frequencies.
        </p>

        <h2 id="outputs">Outputs of Material Models</h2>
        <MathJax>
          <p>
            Material models do{" "}
            <strong>not directly simulate experimental observables</strong> like
            reflectivity, transmission, or Raman intensity. Instead, they output{" "}
            <em>intrinsic optical properties</em> such as:
          </p>

          <ul>
            <li>
              The <strong>dielectric function</strong>{" "}
              {"\\( \\varepsilon(\\omega) \\)"}
            </li>
            <li>
              The <strong>refractive index</strong> {"\\( n + i k \\)"}
            </li>
            <li>
              The <strong>optical conductivity</strong>{" "}
              {"\\( \\sigma(\\omega) \\)"}
            </li>
            <li>
              The <strong>loss function</strong>{" "}
              {
                "\\( \\mathrm{Im}\\left[ -\\frac{1}{\\varepsilon(\\omega)} \\right] \\)"
              }
              , commonly used to analyze energy dissipation and plasmonic
              excitations
            </li>
            <li>
              The <strong>penetration depth</strong>{" "}
              {"\\( \\delta(\\omega) = \\frac{1}{\\alpha(\\omega)} \\)"}, which
              indicates how deep electromagnetic radiation can penetrate the
              material
            </li>
          </ul>

          <p>
            These optical properties are then used by other models, the
            Experiment models, (e.g., Reflectivity (R), Absorption (A),
            Ellipsometry (Ellips), Raman, or Transmission (T) models) to compute
            measurable quantities. Think of the material model with its oscillators as the{" "}
            <strong>foundation</strong> that feeds into the full experimental
            simulation or fit.
          </p>

          <p>
            You can view the output of any material model directly by adding the
            curves to a graph or by linking it to a complete Experiment model.
          </p>
        </MathJax>

        <p><a href="#top">Back to top</a></p>

        <h2>Important Considerations</h2>
        <ul>
          <li>
            Units for frequency-related parameters (e.g.,{" "}
            <MathJax inline>{"\\( \\omega_0 \\)"}</MathJax>,{" "}
            <MathJax inline>{"\\( \\gamma \\)"}</MathJax>) are in{" "}
            <strong>
              cm<sup>-1</sup>
            </strong>
            .
          </li>
          <li>
            Amplitudes typically scale the dielectric response and are unitless
            unless otherwise noted.
          </li>
          <li>
            For most physical models, keep parameters positive. Negative weights
            can be used for advanced effects, but must be handled carefully.
          </li>
          <li>
            Ensure that the total dielectric function remains{" "}
            <strong>causal</strong> and physically meaningful. Standard line
            shapes like Drude and Lorentz are causal by construction. However,
            take extra care when combining {" "}
            <em>custom oscillators</em>, such as those with{" "}
            <strong>negative amplitudes</strong>
            or{" "} <strong> constant ε<sub>2</sub>
            </strong>. These may violate the Kramers-Kronig relations and result in
            unphysical or unstable behavior.
          </li>
          <li>
            Other line shapes than Drude and Lorentz are available, only use them if you know you need it for a physical reason. Beware that some line shapes are not Kramers Kronig consistent!
          </li>
        </ul>

        <h2 id="line-shapes">Available Line Shapes</h2>
        <p>
          These line shapes are currently available in Speqqle.
        </p>

        <h3>Standard: Drude and Lorentz</h3>
        <ul>
          <li>
            <strong>Drude:</strong> Free carrier response with damping, is achieved by fixing <MathJax inline>{"\\( \\omega_o \\)"}</MathJax> to 0. 
            <br />
            <em>Parameters:</em> <MathJax inline>{"\\( \\omega_p \\)"}</MathJax>{" "}
            (plasma frequency), <MathJax inline>{"\\( \\gamma \\)"}</MathJax>{" "}
            (damping) (in cm<sup>-1</sup>). 
          </li>
          <li>
            <strong>Lorentz:</strong> Bound oscillator representing interband
            transitions or phonons.
            <br />
            <em>Parameters:</em> <MathJax inline>{"\\( \\omega_0 \\)"}</MathJax>{" "}
            (resonance), <MathJax inline>{"\\( \\gamma \\)"}</MathJax> (width),{" "}
            <MathJax inline>{"\\( A = \\omega_p^2 \\)"}</MathJax> (amplitude).
          </li>
        </ul>

        <p><a href="#top">Back to top</a></p>

        <h3 id="tauc">Tauc-Lorentz</h3>
        <p>
          The Tauc-Lorentz model is used to describe the dielectric function of amorphous semiconductors and
          disordered materials. It combines the Tauc joint density of states — which captures the optical
          absorption onset — with the Lorentz oscillator, which represents resonance absorption behavior.
        </p>
        <p>
          This model is especially suitable for materials where a band gap is present, but where the electronic
          transitions do not follow sharp edges due to disorder or phonon-assisted absorption.
        </p>
        <p>
  In Speqqle, the Tauc-Lorentz oscillator is defined with the following parameters:
</p>
<ul>
  <li>
    <MathJax inline>{"\\( \\omega_g \\)"}</MathJax> — Tauc band gap energy (in cm<sup>-1</sup>)
  </li>
  <li>
    <MathJax inline>{"\\( \\omega_0 \\)"}</MathJax> — peak transition energy (resonance)
  </li>
  <li>
    <MathJax inline>{"\\( \\gamma \\)"}</MathJax> — broadening (width of the transition)
  </li>
  <li>
    <MathJax inline>{"\\( A \\)"}</MathJax> — amplitude (oscillator strength)
  </li>
  <li>
    <MathJax inline>{"\\( \\varepsilon_\\infty \\)"}</MathJax> — high-frequency dielectric constant (added to real part)
  </li>
</ul>

<p>
  The imaginary part of the dielectric function is constructed to be zero below the band gap (
  <MathJax inline>{"\\( \\omega < \\omega_g \\)"}</MathJax>), and above the gap it follows a modified Lorentzian form
  multiplied by a Tauc joint density of states factor. The real part is then obtained through the
  Kramers–Kronig relation, ensuring causality.
</p>

<details>
  <summary style={{ cursor: "pointer" }}>Advanced Physics: Mathematical Expression</summary>
  <p>
    The imaginary part of the dielectric function 
    <MathJax inline>{"\\( \\varepsilon_2(\\omega) \\)"}</MathJax> in the Tauc-Lorentz model is:
  </p>

  <MathJax>
    {"\\[ \\varepsilon_2(\\omega) = \\begin{cases} \\dfrac{A \\omega_0 \\gamma (\\omega - \\omega_g)^2}{\\omega \\left[ (\\omega^2 - \\omega_0^2)^2 + \\gamma^2 \\omega^2 \\right]} & \\text{for } \\omega > \\omega_g \\\\ 0 & \\text{for } \\omega \\leq \\omega_g \\end{cases} \\]"}
  </MathJax>

  <p>
    The real part <MathJax inline>{"\\( \\varepsilon_1(\\omega) \\)"}</MathJax> is obtained by applying the
    Kramers–Kronig transformation to <MathJax inline>{"\\( \\varepsilon_2(\\omega) \\)"}</MathJax>, which integrates
    contributions over all frequencies.
  </p>
</details>


        <p><a href="#top">Back to top</a></p>

        <h3 id="fano">Fano</h3>
        <p>
          The Fano line shape describes an asymmetric resonance, arising when a discrete optical transition
          interacts with a continuum of states. This interaction leads to constructive or destructive interference,
          producing a peak with an asymmetric profile. The Fano resonance is widely observed in quantum systems,
          solid-state materials, and nano-optical structures.
        </p>
        <p>
          In optical spectroscopy, Fano profiles are particularly useful for describing phonons or excitons that
          couple to electronic continua. Unlike the symmetric Lorentzian shape, the Fano oscillator introduces
          an asymmetry parameter that modifies both the peak height and the shape.
        </p>

        <p>
  In optical spectroscopy, Fano profiles are particularly useful for describing phonons or excitons that
  couple to electronic continua. Unlike the symmetric Lorentzian shape, the Fano oscillator introduces
  an asymmetry parameter that modifies both the peak height and the shape.
</p>

<p>In Speqqle, the Fano oscillator uses the following parameters:</p>
<ul>
  <li>
    <MathJax inline>{"\\( \\omega_0 \\)"}</MathJax> — Resonance (central frequency)
  </li>
  <li>
    <MathJax inline>{"\\( \\gamma \\)"}</MathJax> — Broadening (linewidth)
  </li>
  <li>
    <MathJax inline>{"\\( \\omega_q \\)"}</MathJax> — Inverse asymmetry frequency parameter, related to <MathJax inline>{"\\( q = \\omega_0 / \\omega_q \\)"}</MathJax>
  </li>
  <li>
  <MathJax inline>{"\\( \\omega_p \\)"}</MathJax> — Spectral weight (amplitude)
</li>
</ul>

<details>
<summary style={{ cursor: "pointer" }}>Advanced Physics: Mathematical Expression</summary>
  <p>
    The Fano oscillator in Speqqle implements a phenomenological formula that captures
    an asymmetric Lorentzian shape while preserving <strong>causality</strong>, obeying the {" "}
    <strong>symmetry relation</strong> for <MathJax inline>{"\\( \\omega \\to -\\omega \\)"}</MathJax>, and maintaining 
    {" "}<strong>sum-rule friendliness</strong>.
  </p>

  <p>
    The imaginary part of the dielectric function is:
  </p>

  <MathJax>
  {"\\[ \\varepsilon_2(\\omega) = \\frac{\\omega_p^2}{\\omega_0^2 - \\omega^2 - i \\gamma \\omega} \\left( 1 + i \\frac{\\omega_q}{\\omega} \\right)^2 + \\left( \\frac{\\omega_p \\omega_q}{\\omega_0 \\omega} \\right)^2 \\]"}
</MathJax>


  <p>
    The asymmetry is governed by the ratio {" "}
    <MathJax inline>{"\\( q = \\omega_0 / \\omega_q \\)"}</MathJax>, consistent with the original
    Fano formalism from U. Fano (Phys. Rev. 124, 1866 (1961)).
  </p>

  <p>
    The symmetric Lorentzian line shape is recovered in the limit {" "}
    <MathJax inline>{"\\( \\omega_q \\to 0 \\)"}</MathJax>, where the resonance is centered at {" "}
    <MathJax inline>{"\\( \\omega_0 \\)"}</MathJax>.
  </p>

  <p>
    <strong>Important physical note:</strong> The dielectric loss function {" "}
    <MathJax inline>{"\\( \\varepsilon_2(\\omega) \\)"}</MathJax> in this model can become {" "} <strong>negative</strong> 
    {" "} due to interference effects intrinsic to Fano physics. This is physically allowed,
    but you must ensure that the <strong>total ε₂ remains positive</strong> by using a sufficiently high 
    optical background. This reflects the real physical origin of the asymmetry — 
    interaction between a discrete resonance and a continuum background.
  </p>

  <p>
    The model is derived from a modified version of the expression 
    presented in the PhD thesis of A. Damascelli (University of Groningen, 1996, p. 21).
  </p>
</details>

    

        <p><a href="#top">Back to top</a></p>

        <h3 id="constant">Constant Dielectric Function</h3>
        <p>...</p>
        <p><a href="#top">Back to top</a></p>

        <h3 id="cauchy">Cauchy</h3>
        <p>...</p>
        <p><a href="#top">Back to top</a></p>

        <h3 id="cauchyabs">Cauchy with Absorption</h3>
        <p>...</p>
        <p><a href="#top">Back to top</a></p>

        <h3 id="factorized">Factorized</h3>
        <p>...</p>
        <p><a href="#top">Back to top</a></p>

        <h3 id="negative-drude">Drude-Lorentz with Negative Weight</h3>
        <p>...</p>
        <p><a href="#top">Back to top</a></p>

        <h3 id="differential">Differential Lorentz</h3>
        <p>...</p>
        <p><a href="#top">Back to top</a></p>

        <h3 id="gapped-drude">Gapped Drude</h3>
        <p>...</p>
        <p><a href="#top">Back to top</a></p>

        <h3 id="kkhybrid">KK-Consistent Hybrid Lorentz with Power Law</h3>
        <p>...</p>
        <p><a href="#top">Back to top</a></p>

      </ManualContent>
    </MathJaxContext>
  );
};

export default MaterialModels;